import React from 'react';
import { Chip } from '../ModuleFrame';
import Wireframe from './Wireframe';
import { Shot } from './shared';

// Screen review — one place to see which screens a concept has and to look
// at one at a time ([[lab-system]]). A numbered strip names every screen;
// nothing focused = the clickable wireframe grid; one focused = the large
// wireframe beside a spec card (purpose, primary action, layout regions,
// the flows it appears in, its rendered visual) with prev / next and the
// arrow keys. Pure presentation: the caller owns `selected` (local state
// on the Design/Surfaces/Try panel, the ?screen= URL param on the concept
// board) and any actions (`onBuild`, `onRequestChange` → a revision note
// aimed at this screen).

const KIND_LABEL = { nav: 'navigation', header: 'header', list: 'list', table: 'table', detail: 'details', form: 'form', card: 'cards', chart: 'chart', chat: 'chat', cta: 'call to action', empty: 'empty state', media: 'media', text: 'text' };

export default function ScreenReview({ org, concept, selected, onSelect, onBuild = null, onRequestChange = null, busy = '', compact = false, gallery = false }) {
  const spec = concept?.spec || {};
  const screens = spec.screens || [];
  const visuals = spec.visuals || [];
  const flows = spec.flows || [];
  if (!screens.length && !(gallery && visuals.length)) {
    return <p className="cs-help" style={{ margin: 0 }}>This concept has no screens yet.</p>;
  }
  const idx = screens.findIndex((s) => s.id === selected);
  const cur = idx >= 0 ? screens[idx] : null;
  const prev = idx > 0 ? screens[idx - 1] : null;
  const next = idx >= 0 && idx < screens.length - 1 ? screens[idx + 1] : null;
  const onKey = (e) => {
    if (!cur) return;
    if (e.key === 'ArrowRight' && next) { e.preventDefault(); e.stopPropagation(); onSelect(next.id); }
    if (e.key === 'ArrowLeft' && prev) { e.preventDefault(); e.stopPropagation(); onSelect(prev.id); }
  };

  const strip = screens.length > 0 && (
    <nav className="sr-strip" aria-label="Screens">
      {screens.map((s, i) => (
        <button key={s.id || s.name} type="button" className={`lab-seg-btn ${cur && s.id === cur.id ? 'is-on' : ''}`} onClick={() => onSelect(s.id)} aria-pressed={!!cur && s.id === cur.id} title={s.purpose || s.name}>
          <span className="lab-rail-n">{i + 1}</span> {s.name}
        </button>
      ))}
      {cur && <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => onSelect(null)}>All screens</button>}
    </nav>
  );

  if (!cur) {
    return (
      <div className="sr" onKeyDown={onKey}>
        {strip}
        {gallery && visuals.length > 0 && (
          <div className="cb-gallery">
            {visuals.map((v, i) => (
              <figure key={v.key || i} className="cb-visual">
                <Shot org={org} runId={v.runId} k={v.key} large auto alt={v.screenId || 'Concept image'} />
              </figure>
            ))}
          </div>
        )}
        {screens.length > 0 && (
          <div className={compact ? 'ab-screens is-strip' : 'ab-screens'}>
            {screens.map((s) => <Wireframe key={s.id || s.name} screen={s} compact={compact} onClick={() => onSelect(s.id)} />)}
          </div>
        )}
      </div>
    );
  }

  const regions = cur.layout?.regions || [];
  const inFlows = flows.map((f) => ({ flow: f, steps: (f.steps || []).map((st, i) => ({ ...st, n: i + 1 })).filter((st) => st.screenId === cur.id) })).filter((x) => x.steps.length);
  const visual = visuals.find((v) => v.screenId === cur.id) || null;
  return (
    <div className="sr" onKeyDown={onKey}>
      {strip}
      <div className="sr-focus">
        <Wireframe screen={cur} large />
        <aside className="sr-card" aria-label={`About the ${cur.name} screen`}>
          <div className="sr-card-head">
            <span className="mf-muted cb-small">Screen {idx + 1} of {screens.length}</span>
            <h3>{cur.name}</h3>
            {cur.purpose && <p className="sr-purpose">{cur.purpose}</p>}
            {cur.primaryAction && <div className="lab-compose-row" style={{ gap: 6 }}><span className="mf-muted cb-small">Primary action</span><Chip tone="ok">{cur.primaryAction}</Chip></div>}
          </div>
          <section>
            <h4 className="lab-h">Layout <span className="mf-muted">{regions.length} region{regions.length === 1 ? '' : 's'}</span></h4>
            {regions.length ? (
              <ol className="sr-regions">
                {regions.map((r, i) => (
                  <li key={i}>
                    <Chip>{KIND_LABEL[r.kind] || r.kind}</Chip>
                    <span className="sr-region-text">
                      <strong>{r.label || KIND_LABEL[r.kind] || r.kind}</strong>
                      {r.items?.length ? <span className="mf-muted"> — {r.items.join(' · ')}</span> : null}
                    </span>
                    <span className="mf-muted cb-small sr-span">{r.span === 3 || !r.span ? 'full width' : `${r.span}/3`}</span>
                  </li>
                ))}
              </ol>
            ) : <p className="mf-muted" style={{ margin: 0 }}>No layout yet.</p>}
          </section>
          <section>
            <h4 className="lab-h">Appears in flows <span className="mf-muted">{inFlows.length}</span></h4>
            {inFlows.length ? (
              <ul className="sr-flows">
                {inFlows.map(({ flow, steps }) => (
                  <li key={flow.id}>
                    <strong>{flow.name}</strong>
                    <span className="mf-muted"> — {steps.map((st) => `step ${st.n}: ${st.action}`).join(' · ')}</span>
                  </li>
                ))}
              </ul>
            ) : <p className="mf-muted" style={{ margin: 0 }}>No flow reaches this screen yet.</p>}
          </section>
          {visual && (
            <section>
              <h4 className="lab-h">Rendered visual</h4>
              <figure className="cb-visual">
                <Shot org={org} runId={visual.runId} k={visual.key} large auto alt={`Concept image of ${cur.name}`} />
                {visual.prompt && <figcaption className="mf-muted cb-small lab-clamp2" title={visual.prompt}>{visual.prompt}</figcaption>}
              </figure>
            </section>
          )}
        </aside>
      </div>
      <div className="lab-compose-row sr-nav">
        <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" disabled={!prev} onClick={() => prev && onSelect(prev.id)} title={prev ? prev.name : undefined}>← {prev ? prev.name : 'Prev'}</button>
        <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" disabled={!next} onClick={() => next && onSelect(next.id)} title={next ? next.name : undefined}>{next ? next.name : 'Next'} →</button>
        <span style={{ flex: 1 }} />
        {onRequestChange && <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => onRequestChange(cur)} data-nova-note="Opens the concept's revision panel with a note aimed at this screen" title="Say what should change on this screen; the note joins the next revision of the concept">Request a change</button>}
        {onBuild && <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" disabled={busy === `build-screen-${cur.id}`} onClick={() => onBuild(cur)} data-nova-note="Sends this one screen to the instance's Dev Console as a small build" title="Send this screen to the Dev Console; a coding-agent session builds it from the concept and opens a pull request">{busy === `build-screen-${cur.id}` ? 'Sending… (about a minute)' : 'Build this screen'}</button>}
      </div>
    </div>
  );
}
